/**
 * @file LicenseQuickFilter.tsx
 * @desc Segmented quick-filter bar for the licenses table
 * @copyright All rights reserved @ Inarix
 */

import { FC, useEffect } from "react";
import { Box, Chip } from "@mui/material";
import { TFunction } from "i18next";
import {
  containerSx,
  labelBoxSx,
  countBadgeSx,
  chipSx,
  labelTextStyle,
} from "./licenseStyles/LicenseQuickFilter.styles";
import { InarixGridFilterModel } from "../../redux/slices/dataTable/data";

type FilterOption = {
  key: string;
  label: string;
  count: number;
  color: string;
  bg: string;
};

type LicenseQuickFilterProps = {
  filterOptions: FilterOption[];
  activeFilter: string;
  onFilterChange: (key: string) => void;
  filterModel: InarixGridFilterModel;
  setFilterModels: (model: InarixGridFilterModel) => void;
  t: TFunction<"global", undefined>;
};

export const LicenseQuickFilter: FC<LicenseQuickFilterProps> = ({
  filterOptions,
  activeFilter,
  onFilterChange,
  filterModel,
  setFilterModels,
  t,
}) => {
  // Map chip keys to the values the filter selector understands

  const filterKeyToValue: Record<string, string> = {
    active: "active",
    unassigned: "unassigned",
    upcoming: "upcoming",
    expired: "expired",
  };

  const manageFilterModel = (filterName: string) => {
    const filterValue = filterKeyToValue[filterName] ?? filterName;
    const existingFilter = filterModel?.items?.find(
      (item) => item.field === "licensesStatus",
    );

    if (existingFilter && existingFilter.value === filterValue) {
      setFilterModels({
        ...filterModel,
        items: filterModel.items.filter(
          (item) => item.field !== "licensesStatus",
        ),
      });
    } else {
      const otherItems = (filterModel?.items || []).filter(
        (item) => item.field !== "licensesStatus",
      );
      setFilterModels({
        ...filterModel,
        items: [
          ...otherItems,
          {
            column: t("licenses.licensesStatus"),
            columnType: "",
            field: "licensesStatus",
            filter: "LOCAL",
            queryFormat: "",
            service: "special-licenses",
            operator: "$eq",
            realOperator: "$eq",
            value: filterValue,
          },
        ],
      });
    }
  };

  useEffect(() => {
    if (activeFilter === "all") {
      setFilterModels({
        ...filterModel,
        items: (filterModel?.items || []).filter(
          (item) => item.field !== "licensesStatus",
        ),
      });
    } else {
      manageFilterModel(activeFilter);
    }
  }, []);

  return (
    <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
      <Box sx={{ ...containerSx, mb: 0 }}>
        {filterOptions.map(({ key, label, count, color, bg }) => {
          const isSelected = activeFilter === key;
          return (
            <Chip
              key={key}
              onClick={() => {
                const isAlreadySelected = activeFilter === key;
                onFilterChange(isAlreadySelected ? "all" : key);
                if (key === "all" || isAlreadySelected) {
                  setFilterModels({
                    ...filterModel,
                    items: (filterModel?.items || []).filter(
                      (item) => item.field !== "licensesStatus",
                    ),
                  });
                } else {
                  manageFilterModel(key);
                }
              }}
              label={
                <Box sx={labelBoxSx}>
                  <span style={labelTextStyle(isSelected)}>{label}</span>
                  <Box sx={countBadgeSx(bg, color)}>{count}</Box>
                </Box>
              }
              sx={chipSx(isSelected)}
            />
          );
        })}
      </Box>
    </Box>
  );
};
